import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Form, Input, notification, Row, Space, Typography } from 'antd'
import { FormListFieldData } from 'antd/lib/form/FormList'
import {
	ICertification,
	TExpertProfile,
	TExpertAccountRequest,
	TKeyword,
	TExpertPage,
} from '../../../../../context/types'
import SortableCertifications from './SortableCertifications'
import { useExpertProfilesContext } from '../../../../../context/expertProfile'
import { PlusOutlined } from '@ant-design/icons'
import { useAuthContext } from '../../../../../context/auth'
import { sortCertifications } from '../../../../../utils/sort'
import BioEditor from './BioEditor'
import { usePageLeaveContext } from '../../../../../context/pageLeave'
import KeywordsSelect from '../../../../../components/helpers/KeywordsSelect/KeywordsSelect'
import { useAccountContext } from '../../../../../context/account'

const { TextArea } = Input
const { Paragraph, Title } = Typography

export type TExpertProfileForm = {
	profile: TExpertProfile
	expertPage: TExpertPage
}

function ExpertProfileForm({ profile, expertPage }: TExpertProfileForm) {
	const [form] = Form.useForm()
	const { expertProfiles, actions } = useExpertProfilesContext()
	const { auth } = useAuthContext()
	const { pageLeave, actions: pageLeaveActions } = usePageLeaveContext()
	const { account } = useAccountContext()
	const [formErrors, setFormErrors] = useState([])
	const initialValues = useMemo<{ [key: string]: any }>(
		() => ({
			bio: expertPage?.bio,
			title: expertPage?.title,
			body: expertPage?.body,
			keywords: expertPage?.keywords.map((k: TKeyword) => k.keyword) || [],
			certifications: auth?.user?.certifications?.sort(sortCertifications) || [],
		}),
		[expertPage?.bio, profile?.pages, auth?.user?.certifications, expertPage?.id]
	)
	const isFormDirtyDeps = [expertPage?.bio, profile?.pages, auth?.user?.certifications, form.getFieldsValue()]
	const isFormDirty = useMemo(() => {
		const values = form.getFieldsValue()
		return Object.keys(values).reduce((isDirty: boolean, key: string) => {
			return isDirty || values[key] !== initialValues[key]
		}, false)
	}, isFormDirtyDeps)

	useEffect(() => {
		pageLeaveActions.setConditions({
			...pageLeave.conditions,
			expert: isFormDirty,
		})
	}, [isFormDirty])

	useEffect(() => {
		form.setFieldsValue(initialValues)
		setFormErrors([])
	}, [form, initialValues, expertPage?.id])

	const onFinish = (values: TExpertAccountRequest) => {
		actions
			.updateExpertAccount(profile.id, { ...values, channel_id: expertPage.channel_id }, account?.token)
			.then((response) => {
				if (response.ok) {
					notification.success({
						message: 'Successfully Saved',
					})
				}
			})
	}

	const addRankToCertifications = () => {
		const values = form.getFieldsValue()
		form.setFieldsValue({
			certifications: values.certifications.map((certification: ICertification, index: number) => ({
				...certification,
				rank: index,
			})),
		})
	}

	const onFieldsChange = (changedFields: any[]) => {
		if (changedFields[0]?.name.includes('certifications')) {
			addRankToCertifications()
		}

		const nextFormErrors = form.getFieldsError().filter((v) => v.errors.length)
		setFormErrors(nextFormErrors)
	}

	const onChangeKeywords = (keywords: any) => {
		const values = form.getFieldsValue()
		form.setFieldsValue({
			...values,
			...keywords,
		})
		form.validateFields(['keywords'])
	}

	return (
		<Form
			name="ExpertProfileForm"
			form={form}
			scrollToFirstError
			layout="vertical"
			initialValues={initialValues}
			onFinish={onFinish}
			onFieldsChange={onFieldsChange}
		>
			<Row gutter={[50, 0]}>
				<Col xs={24} lg={12}>
					<Form.Item
						label="Headline (eg. Helping mid-career managers get to the next level or World-class SEO expert for small businesses). If you can’t think of a headline, please use your title."
						name="title"
						rules={[
							{ required: true, message: 'Please input page title!' },
							{ max: 100, message: 'Max length is 100 chars.' },
						]}
					>
						<TextArea rows={1} showCount maxLength={100} autoSize />
					</Form.Item>
					<Form.Item
						label="Short description (This shows up in search results)"
						name="body"
						rules={[
							{ required: true, message: 'Please fill short description!' },
							{ max: 1000, message: 'Max length is 1000 chars.' },
						]}
					>
						<TextArea rows={4} showCount maxLength={1000} />
					</Form.Item>
					<Form.Item
						label="About You / Bio (This is what members see when they view your profile)"
						name="bio"
						rules={[{ required: true, message: 'Please input your full profile!' }]}
					>
						<BioEditor form={form} />
					</Form.Item>
				</Col>
				<Col xs={24} lg={12}>
					<Space direction="vertical" style={{ width: '100%' }}>
						<Title level={4}>Certifications</Title>
						<Form.List name="certifications">
							{(fields: FormListFieldData[], { add, remove }) => (
								<>
									<SortableCertifications fields={fields} remove={remove} form={form} />
									<Row>
										<Col span={24}>
											<Form.Item>
												<Button type="ghost" onClick={() => add()} icon={<PlusOutlined />}>
													Add Certifications
												</Button>
											</Form.Item>
										</Col>
									</Row>
								</>
							)}
						</Form.List>
					</Space>
					<Form.Item
						label="Keywords (Enter up to 15 keywords that refer to areas in only which you have deep expertise)"
						name="keywords"
						rules={[{ required: true, message: 'Please input page keywords!' }]}
					>
						<KeywordsSelect
							selectedOptions={expertPage?.keywords ? [...expertPage.keywords] : []}
							selectedKeywords={form.getFieldValue('keywords')}
							onChangeKeywords={onChangeKeywords}
							notFoundContent="Please start typing.."
						/>
					</Form.Item>
				</Col>
			</Row>
			<Space>
				<Button shape="round" type="primary" htmlType="submit" loading={!!expertProfiles.loading}>
					Update
				</Button>
			</Space>
			{/* {auth.error && <Alert message="Error" description={auth.error} type="error" />} */}
			{formErrors.length > 0 && (
				<Alert
					showIcon
					type="warning"
					message="Warning"
					style={{ marginTop: 20 }}
					description={formErrors.map((v) => (
						<Paragraph key={v.name + '_errors'} style={{ marginBottom: 0 }}>
							{v.errors[0]}
						</Paragraph>
					))}
				/>
			)}
		</Form>
	)
}

export default ExpertProfileForm
